import { useCallback } from 'react';

import {
  applyVideoSendParameters,
  ensureSendableVideoTrack,
  getVideoSender,
  markVideoTrackAsMotion,
} from './useLocalVideoHelpers';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { LogLevel } from 'features/analytics/enums';
import { sendAnalytics } from 'features/analytics/store';
import { useAppDispatch } from 'features/app/hooks';
import { useMediaStream } from 'features/media-streams/hooks';
import type { RtcConnection } from 'features/sip/interfaces';

type PrivacyTrack = MediaStreamTrack & {
  mercuryPrivacyTrack?: boolean;
};

export const useLocalVideo = () => {
  const { getInterpVideoStream, getPrivacyMediaStreamTrack } = useMediaStream();
  const dispatch = useAppDispatch();

  const stopDetachedPrivacyTrack = (
    previousTrack: MediaStreamTrack | null,
    nextTrack: MediaStreamTrack | null
  ) => {
    const previousPrivacyTrack = previousTrack as PrivacyTrack | null;

    if (
      previousPrivacyTrack &&
      previousPrivacyTrack !== nextTrack &&
      previousPrivacyTrack.mercuryPrivacyTrack &&
      typeof previousPrivacyTrack.stop === 'function'
    ) {
      previousPrivacyTrack.stop();
    }
  };

  const isConnectionClosed = (rtcConnection: RtcConnection) =>
    rtcConnection.signalingState === 'closed' ||
    rtcConnection.connectionState === 'closed';

  const sendPrivacyTrackToRemote = useCallback(
    async (rtcConnection: RtcConnection) => {
      const localRtpVideo = getVideoSender(rtcConnection);
      const previousTrack = localRtpVideo.track;
      const privacyTrack = getPrivacyMediaStreamTrack();

      await localRtpVideo.replaceTrack(privacyTrack);
      await applyVideoSendParameters(localRtpVideo);

      stopDetachedPrivacyTrack(previousTrack, privacyTrack);
    },
    [getPrivacyMediaStreamTrack]
  );

  const sendVideoTrackToRemote = useCallback(
    async (rtcConnection: RtcConnection, stream?: MediaStream) => {
      const localRtpVideo = getVideoSender(rtcConnection);
      const previousTrack = localRtpVideo.track;

      const usedStream = stream ?? (await getInterpVideoStream());
      const localVideoTrack = ensureSendableVideoTrack(
        usedStream.getVideoTracks()[0]
      );

      markVideoTrackAsMotion(localVideoTrack);

      await localRtpVideo.replaceTrack(localVideoTrack);
      await applyVideoSendParameters(localRtpVideo);
      stopDetachedPrivacyTrack(previousTrack, localVideoTrack);

      dispatch(
        sendAnalyticsInfo({
          Method: 'sendVideoTrackToRemote',
          Message:
            'Sent local video track to remote with validated live track state',
          TrackId: localVideoTrack.id,
          TrackReadyState: localVideoTrack.readyState,
          TrackEnabled: localVideoTrack.enabled,
          TrackMuted: localVideoTrack.muted,
        })
      );
    },
    [dispatch, getInterpVideoStream]
  );

  const stopRtcTracksAndCloseConnection = useCallback(
    (rtcConnection: RtcConnection) => {
      // Detach the local video feeds; note that we do NOT stop the tracks here since they are
      // most likely being managed through the SourceMediaTracks and are likely to be used elsewhere
      rtcConnection.getSenders().forEach((sender) => {
        if (sender.track) {
          void sender.replaceTrack(null).catch(() => {
            // Connection teardown is best-effort; replacing track can reject after close.
          });
        }
      });

      // stop the incoming video streams
      rtcConnection.getReceivers().forEach((receiver) => {
        receiver.track?.stop();
      });
      if (!isConnectionClosed(rtcConnection)) {
        dispatch(
          sendAnalytics({
            Method: 'stopRtcTracksAndCloseConnection',
            Message: 'RtcConnection is not closed at this point, closing it',
            Level: LogLevel.WARNING,
          })
        );
        rtcConnection.close();
      }
    },
    [dispatch]
  );

  return {
    sendPrivacyTrackToRemote,
    sendVideoTrackToRemote,
    stopRtcTracksAndCloseConnection,
  };
};
